import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { eosTool } from '@/services/blockchain/eos-tool'
import { getChainId } from '@/services/blockchain/networks'
import { decadd, decdiv } from '@/lib/decimal'
import type { SwapConfig } from '@/types'

interface ConfigState {
  rpcUrl: string
  config: SwapConfig
  isDev: boolean
  slippageTolerance: number
  isMultipath: boolean
  isMaintenance: boolean
  setRpcUrl: (url: string) => void
  setSlippageTolerance: (value: number) => void
  setIsMultipath: (value: boolean) => void
  setIsDev: (value: boolean) => void
  fetchConfig: () => Promise<void>
}

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      rpcUrl: 'https://eospush.mytokenpocket.vip',
      config: { status: 0, tradeFee: 0, protocolFee: 0, totalFee: '0', totalFeeFloat: '0', totalFeePercent: '0%' },
      isDev: false,
      slippageTolerance: 5,
      isMultipath: true,
      isMaintenance: false,
      setRpcUrl: (url) => {
        eosTool.updateChainId(getChainId(url))
        eosTool.updateRpc(url)
        set({ rpcUrl: url })
      },
      setSlippageTolerance: (value) => set({ slippageTolerance: value }),
      setIsMultipath: (value) => set({ isMultipath: value }),
      setIsDev: (value) => set({ isDev: value }),
      fetchConfig: async () => {
        const params = { code: 'swap.ttr', scope: 'swap.ttr', table: 'configs', json: true }
        const rows = await eosTool.getTableRows<{ key: string; value: number }>(params)
        if (rows?.length) {
          const config: SwapConfig = { status: 0, tradeFee: 0, protocolFee: 0, totalFee: '0', totalFeeFloat: '0', totalFeePercent: '0%' }
          for (const row of rows) {
            if (row.key === 'status') config.status = row.value
            else if (row.key === 'fee.protocol') config.protocolFee = row.value
            else if (row.key === 'fee.trade') config.tradeFee = row.value
          }
          config.totalFee = decadd(config.tradeFee, config.protocolFee)
          config.totalFeeFloat = decdiv(config.totalFee, 10000)
          config.totalFeePercent = decdiv(config.totalFee, 100) + '%'
          set({ config, isMaintenance: config.status !== 1 })
        }
      },
    }),
    {
      name: 'totoro-config',
      partialize: (state) => ({
        rpcUrl: state.rpcUrl,
        isDev: state.isDev,
        slippageTolerance: state.slippageTolerance,
        isMultipath: state.isMultipath,
      }),
    },
  ),
)
